import Component from '@bythepixel/component-loader';
import { breakpoints } from '@Front/constants';
import HideableElement from '@Core/components/hideableElement';
import GlideSlider from '../base';

const glideOptions = {
  breakpoints: {
    [breakpoints['break-1']]: {
      focusAt: 'center',
      dragThreshold: 120,
    },
    [breakpoints['break-2'] - 1]: {
      gap: 12.5,
      focusAt: 'center',
      dragThreshold: 120,
    },
    [breakpoints['break-2']]: {
      focusAt: 0,
    },
  },
  dragThreshold: false,
  gap: 16,
  perTouch: 1,
  rewind: false,
  type: 'slider',
};

const fullWidthGlideOptions = {
  breakpoints: {
    [breakpoints['break-1']]: {
      dragThreshold: 120,
    },
    [breakpoints['break-2'] - 1]: {
      gap: 12.5,
      dragThreshold: 120,
    },
  },
  dragThreshold: false,
  focusAt: 0,
  gap: 16,
  perTouch: 1,
  rewind: false,
  type: 'slider',
};

const selectors = {
  leftArrow: '[data-comp-vehicle-slider="arrow-left"]',
  rightArrow: '[data-comp-vehicle-slider="arrow-right"]',
  slide: '[data-comp-vehicle-slider="slide"]',
  slider: '[data-comp-vehicle-slider="slider"]',
};

export default class SectionVehicleSlider extends Component {
  static selector = '[data-comp-vehicle-slider="section"]';
  static loaderPriority = 'in-view';

  constructor(container) {
    super(container);
    this.isFullWidth = this.$container.hasAttribute('data-comp-vehicle-slider-full-width');
    this.isYoutubePage = this.$container.dataset.compIsYoutubePage === 'true' || false;

    this.slider = new GlideSlider(
      this.$container.querySelector(selectors.slider),
      this.isFullWidth ? fullWidthGlideOptions : glideOptions,
      selectors,
    );

    // Glide instance of the slider
    this.glideSlider = this.slider.getGlideSlider();

    // DOM slider element
    this.sliderEl = this.slider.getSliderElement();
    this.leftArrow = this.sliderEl.querySelector(selectors.leftArrow);
    this.lastSlide = this.sliderEl.querySelector('.vehicle-card:last-child');
    this.rightArrow = this.sliderEl.querySelector(selectors.rightArrow);
    this.text = new HideableElement('fade-up', this.$container);
    this.hideableElementExists = this.text.element;

    this.initMountEvents();
    this.initSwipeAndMouseEvents();
    this.initResizeEvents();

    if (this.isFullWidth) {
      this.glideSlider.on('run.before', () => {
        this.updatePerView();
      });
    }

    // Mount glide instance after initializing everything
    this.glideSlider.mount();
  }

  /**
   * Handles arrow button visibility
   * This functionality is unique from the base class
   *
   * @return void
   */
  handleArrowButtonVisibility() {
    // Hide left arrow if we're on the first slide
    if (this.leftArrow && this.glideSlider.index === 0) {
      this.leftArrow.style.display = 'none';
    } else if (this.leftArrow) {
      this.leftArrow.style.display = '';
    }

    if (this.lastSlide) {
      // Observer watches the last slide
      // And hides the right arrow when the last slide is in view
      const observer = new IntersectionObserver(entries => {
        const entry = entries[0];
        const { width } = entry.rootBounds; // Viewport width
        const lastSlideRightBound = Math.floor(entry.boundingClientRect.right); // Right bound of last slide
        const sliderRightBound = Math.floor(this.sliderEl.getBoundingClientRect().right);

        // If the width is greater than the position of the last slide's right bound
        // It means the last slide is visible, therefore hide the right arrow
        if (this.rightArrow && !this.isFullWidth && width > lastSlideRightBound) {
          this.rightArrow.style.display = 'none';
        } else if (this.rightArrow && this.isFullWidth && sliderRightBound >= lastSlideRightBound) {
          this.rightArrow.style.display = 'none';
        } else if (this.rightArrow) {
          this.rightArrow.style.display = '';
        }
      });

      observer.observe(this.lastSlide);
    }
  }

  /**
   * Initializes mount events for the vehicle slider container
   *
   * @return void
   */
  initMountEvents() {
    // Run these events after mounting the slider
    this.glideSlider.on('mount.after', () => {
      this.handleArrowButtonVisibility();
      this.updatePerView();
      this.slider.handleSlideIndicatorActiveState();
    });
  }

  /**
   * There are bugs with the breakpoints that require the slider to be updated manually like this
   *
   * @return void
   */
  initResizeEvents() {
    this.glideSlider.on('resize', () => {
      this.updatePerView();

      // Desktop view
      if (window.matchMedia(`(min-width: ${breakpoints['break-2']}px)`).matches) {
        this.glideSlider.update(glideOptions);
        this.handleArrowButtonVisibility();

        // Fade the text if we're already pass the first slide
        if (this.hideableElementExists) {
          if (this.glideSlider.index > 0) {
            this.text.hide();
          } else {
            this.text.show();
          }
        }
      } else {
        // Mobile view
        this.glideSlider.update(glideOptions);
        this.slider.handleSlideIndicatorActiveState();

        if (this.hideableElementExists) {
          this.text.show();
        }
      }
    });
  }

  /**
   * Initializes swipe and mouse movement events
   *
   * @return void
   */
  initSwipeAndMouseEvents() {
    // Mouse dragging/clicking interaction event listener
    this.glideSlider.on('run', () => {
      this.slider.handleSlideIndicatorActiveState();

      if (this.hideableElementExists) {
        if (this.glideSlider.index > 0
          && window.matchMedia(`(min-width: ${breakpoints['break-2']}px)`).matches) {
          this.text.hide();
        } else if (this.glideSlider.index === 0) {
          this.text.show();
        }
      }
    });

    // At the end of the dragging/clicking event
    this.glideSlider.on('run.after', () => {
      this.handleArrowButtonVisibility();
    });

    // Swipe event listener
    this.glideSlider.on('swipe.move', () => {
      // In the glide event swipe.move, the index for the next slide has not been calculated yet
      // Which is why we check if it's still on 0
      if (this.hideableElementExists && this.glideSlider.index === 0
        && window.matchMedia(`(min-width: ${breakpoints['break-2']}px)`).matches) {
        this.text.hide();
      }
    });

    // At the end of the swipe event
    // If the index is 0, it means we're on the first slide, therefore show the text
    this.glideSlider.on('swipe.end', () => {
      if (this.hideableElementExists && this.glideSlider.index === 0) {
        this.text.show();
      }
    });
  }

  /**
   * perView is a glide setting
   * It is dynamically calculated for this slider
   *
   * @return void
   */
  updatePerView() {
    const sliderWidth = this.slider.getSliderWidth();
    const cardWidth = this.isYoutubePage ? 270 : 348;
    const baseExpression = sliderWidth / cardWidth;

    const targetBreakpoints = {
      mobileSm: window.matchMedia('(max-width: 370px)').matches,
      mobile: window.matchMedia('(max-width: 600px)').matches,
      custom: window.matchMedia('(min-width: 600px)').matches,
      tablet: window.matchMedia('(min-width: 768px)').matches,
      desktop: window.matchMedia('(min-width: 1024px)').matches,
      desktopMd: window.matchMedia('(min-width: 1200px)').matches,
    };

    const updatedOptions = {
      perView: 1,
    };

    if (!this.isYoutubePage) {
      // At each breakpoint, the base expression is multiplied by
      // a coefficient that helps achieve certain slide widths. These
      // coefficients are the product of trial and error.
      if (targetBreakpoints.mobileSm) {
        updatedOptions.perView = baseExpression * 1.2;
      } else if (targetBreakpoints.mobile) {
        updatedOptions.perView = baseExpression * 1.1;
      } else if (targetBreakpoints.desktop) {
        updatedOptions.perView = baseExpression * 0.95;
      } else {
        updatedOptions.perView = baseExpression;
      }

      this.glideSlider.update(updatedOptions);
      return;
    }

    /** Handle YoutubePage  */
    updatedOptions.perView = 1;

    if (targetBreakpoints.tablet) {
      updatedOptions.perView = 2;
    }

    if (targetBreakpoints.desktopMd) {
      updatedOptions.perView = 3;
    }

    this.glideSlider.update(updatedOptions);
  }
}
